import * as Sentry from "@sentry/react";
import { print } from "graphql";
import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import {
  DefaultError,
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryOptions,
} from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useAuthStore } from "@/lib/Auth/authStore";
import { QueryClient } from "@/lib/ReactQuery/QueryClient";
import { useLocale } from "@/lib/i18n/useLocale";

import { GraphQLClientError } from "@/lib/GraphQLCodegen/GraphQLClientError";

export type Variables = Record<string, unknown>;

type GraphQLResponse<TData> = {
  data?: TData;
  errors?: Array<{ message: string; [key: string]: unknown }>;
};

const endpoint = import.meta.env.VITE_API_URL;

export class NetworkError extends Error {
  constructor(
    public queryName: string,
    public originalError: Error,
  ) {
    super(`Network error during "${queryName}": ${originalError.message}`);
    this.name = "NetworkError";
  }
}

export async function query<TData, TVariables extends Variables | undefined>(
  queryDoc: TypedDocumentNode<TData, TVariables>,
  variables: TVariables,
  organisationId?: string | null,
  additionalHeaders?: Record<string, string>,
): Promise<TData> {
  const name = getQueryName(queryDoc);

  return Sentry.startSpan(
    { op: getQueryType(queryDoc), name },
    async (span) => {
      try {
        const token = useAuthStore.getState().token;

        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          Accept: "application/json",
          "sentry-trace": Sentry.spanToTraceHeader(span),
          ...additionalHeaders,
        };

        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
        if (organisationId) {
          headers["X-Organisation-Id"] = String(organisationId);
        }

        const baggage = Sentry.spanToBaggageHeader(span);
        if (baggage) {
          headers["baggage"] = baggage;
        }

        const response = await fetch(
          new URL(`/graphql?${name}`, endpoint).toString(),
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              query: print(queryDoc),
              variables: variables ?? undefined,
            }),
          },
        );

        if (!response.ok) {
          const errorBody = (await response.json().catch(() => null)) as {
            code?: string;
            message?: string;
          } | null;

          if (errorBody?.code === "INVALID_ORGANISATION") {
            window.location.href = "/org";
            throw new Error(errorBody.message ?? "Invalid organisation");
          }

          if (response.status === 401) {
            useAuthStore.getState().clearAuth();
            window.location.href = "/sign-in";
            throw new Error("Session expired");
          }

          throw new Error(
            `HTTP ${response.status}: ${response.statusText} for "${name}"`,
          );
        }

        const json = (await response.json()) as GraphQLResponse<TData>;

        if (json.errors?.length) {
          throw new GraphQLClientError(json);
        }

        return json.data as TData;
      } catch (error) {
        // GraphQL application errors (validation, authorization, etc.) are expected
        // and handled by the UI — don't report them to Sentry.
        if (error instanceof GraphQLClientError) {
          throw error;
        }

        // "Load failed" is Safari's message for aborted requests during navigation
        if (error instanceof TypeError) {
          const isNavigationAbort =
            error.message.includes("Load failed") ||
            error.message.includes("cancelled") ||
            error.message.includes("fetch");

          // Silently ignore errors during page unload - these are expected
          if (isNavigationAbort && window.__isUnloading) {
            throw error;
          }

          if (isNavigationAbort) {
            const networkError = new NetworkError(name, error);
            Sentry.withScope((scope) => {
              scope.setTag("error_type", "network");
              scope.setTag("query_name", name);
              scope.setFingerprint(["network-error", name]);
              Sentry.captureException(networkError);
            });
            console.error(networkError);
            throw networkError;
          }
        }

        Sentry.withScope((scope) => {
          scope.setFingerprint([
            JSON.stringify(
              getQueryKey(queryDoc, variables, organisationId ?? null),
            ),
          ]);
          Sentry.captureException(error);
        });

        console.error(error);

        throw error;
      }
    },
  );
}

/**
 * The active org id from the scoped route param (`/$tenant/$orgId/...`, plus the
 * `/org/$orgId/create-supplier` flow). The single source for both the
 * request header (useFetchData) and the cache key (getQueryKey), so a component
 * read and a loader prefetch land on the exact same org-scoped key.
 */
export function useOrgId(): string | null {
  const { orgId } = useParams({ strict: false }) as { orgId?: string };
  return orgId ?? null;
}

export const useFetchData = <TData, TVariables extends Variables | undefined>(
  document: TypedDocumentNode<TData, TVariables>,
): ((variables: TVariables) => Promise<TData>) => {
  const locale = useLocale();
  const organisationId = useOrgId();

  return (variables): Promise<TData> => {
    return query(document, variables, organisationId, {
      "Accept-Language": locale,
    });
  };
};

export function useGqlQuery<TData, TVariables extends Variables | undefined>(
  query: TypedDocumentNode<TData, TVariables>,
  variables: NoInfer<TVariables>,
  options?: Partial<UseQueryOptions>,
): UseQueryResult<TData> {
  const fetcher = useFetchData(query);
  const orgId = useOrgId();
  return useQuery({
    queryKey: getQueryKey(query, variables, orgId),
    queryFn: () => fetcher(variables),
    ...options,
  }) as UseQueryResult<TData>;
}

export function useSuspenseGqlQuery<
  TData,
  TVariables extends Variables | undefined,
>(
  query: TypedDocumentNode<TData, TVariables>,
  variables: TVariables,
  options?: NoInfer<Partial<UseSuspenseQueryOptions<TData, DefaultError>>>,
) {
  const fetcher = useFetchData(query);
  const orgId = useOrgId();
  return useSuspenseQuery({
    queryKey: getQueryKey(query, variables, orgId),
    queryFn: () => fetcher(variables),
    ...options,
  });
}
export function useNullableSuspenseGqlQuery<
  TData,
  TVariables extends Variables | undefined,
>(
  query: TypedDocumentNode<TData, TVariables>,
  variables: TVariables,
  isEnabled: boolean,
  options?: Partial<UseSuspenseQueryOptions<TData | null, DefaultError, TData>>,
) {
  const fetcher = useFetchData(query);
  const orgId = useOrgId();

  return useSuspenseQuery<TData | null, DefaultError, TData>({
    queryKey: getQueryKey(query, variables, orgId),
    queryFn: () => (isEnabled ? fetcher(variables) : null),
    ...options,
  });
}

export function useInfiniteGqlQuery<
  TData,
  TVariables extends Variables | undefined,
>(
  query: TypedDocumentNode<TData, TVariables>,
  variables: TVariables,
  options: Omit<
    UseInfiniteQueryOptions<
      TData,
      DefaultError,
      InfiniteData<TData, { page: number }>
    >,
    "queryFn" | "queryKey"
  >,
) {
  const fetcher = useFetchData(query);
  const orgId = useOrgId();
  return useInfiniteQuery({
    queryKey: getQueryKey(query, variables, orgId, true),
    queryFn: ({ pageParam }: any) =>
      fetcher({
        ...variables,
        ...pageParam,
      }),
    placeholderData: keepPreviousData,
    ...options,
  });
}

export function getQueryKey<TData, TVariables extends Variables | undefined>(
  query: TypedDocumentNode<TData, TVariables>,
  variables: TVariables,
  // Required: org-scope every key so one org's cache can never satisfy another's
  // read. Components pass useOrgId(); loaders/guards pass the URL orgId.
  // Pass null only for genuinely org-agnostic queries.
  organisationId: string | null,
  isInfinite?: boolean,
): (string | TVariables | null)[] {
  let queryName = getQueryName(query);
  if (isInfinite) {
    queryName += `.Infinite`;
  }

  return [queryName, variables, organisationId];
}

function getQueryName(query: TypedDocumentNode<any, any>) {
  return (query.definitions[0] as any).name.value;
}

function getQueryType(query: TypedDocumentNode<any, any>) {
  const definition = query.definitions[0] as any;

  if (definition.operation === "query") {
    return "http.graphql.query";
  }

  return "http.graphql.mutation";
}

export function useGqlMutation<TData, TVariables extends Variables | undefined>(
  query: TypedDocumentNode<TData, TVariables>,
  options?: Partial<UseMutationOptions>,
): UseMutationResult<TData, DefaultError, TVariables> {
  const fetcher = useFetchData(query);
  return useMutation<any, any, any>({
    mutationFn: (variables: TVariables) => fetcher(variables) as any,
    ...options,
  });
}

export function prefetchInfiniteQuery<
  TData,
  TVariables extends Variables | undefined,
>(
  document: TypedDocumentNode<TData, TVariables>,
  variables: TVariables,
  // Required: org scope rides on the request header, not the query key. Every
  // loader call must pass the URL's orgId (`params.orgId`) so the compiler flags
  // any miss instead of silently sending an org-less request.
  organisationId: string | null,
): Promise<any> {
  const infiniteKey = getQueryKey(document, variables, organisationId, true);

  return (
    QueryClient.getQueryData(infiniteKey) ??
    QueryClient.fetchInfiniteQuery({
      initialPageParam: {
        page: 1,
      },
      queryKey: infiniteKey,
      queryFn: () => query(document, variables, organisationId),
    })
  );
}

export function fetchQuery<TData, TVariables extends Variables | undefined>(
  document: TypedDocumentNode<TData, TVariables>,
  variables: TVariables,
  // Required — see prefetchInfiniteQuery: pass the URL's orgId (`params.orgId`).
  organisationId: string | null,
): Promise<TData> {
  return QueryClient.ensureQueryData({
    queryKey: getQueryKey(document, variables, organisationId),
    queryFn: () => query(document, variables, organisationId),
  });
}

export function prefetchQuery<TData, TVariables extends Variables | undefined>(
  document: TypedDocumentNode<TData, TVariables>,
  variables: TVariables,
  organisationId: string | null,
): void {
  fetchQuery(document, variables, organisationId).catch(() => {});
}
